import React from "react";
import PropTypes from "prop-types";
import Chip from "./Chip";

// Single-select sibling of ChipMultiSelect, for filters where the options are mutually
// exclusive — the TAT dashboard's "Sort by" and "Status" rows, which the prototype draws
// as one-of-N chips rather than toggles.
//
// It is a radiogroup, not a row of toggle buttons: with `aria-pressed` on each chip a
// screen reader announces six independent on/off controls and gives no clue that
// choosing one clears the rest. `role="radio"` + `aria-checked` says the true thing.
//
// An option may carry `disabled` and a `title` explaining why — used for sorts the
// backend cannot currently honour. A control that looks live and silently does nothing
// is worse than one that says why it is off.
function ChipSingleSelect({ options, value, onChange, label, inline }) {
    return (
        <div className={inline ? "flex flex-wrap gap-4 items-center" : ""}>
            {label && (
                <p className={`text-sm font-bold font-sans text-black ${inline ? "" : "mb-1.5"}`}>{label}</p>
            )}
            <div className="flex gap-4 items-center justify-start flex-wrap" role="radiogroup" aria-label={label}>
                {options.map((option) => (
                    <Chip
                        key={option.id}
                        active={value === option.id}
                        disabled={Boolean(option.disabled)}
                        title={option.title}
                        onClick={() => onChange(option.id)}
                        role="radio"
                        ariaChecked={value === option.id}
                    >
                        {option.label}
                    </Chip>
                ))}
            </div>
        </div>
    );
}

ChipSingleSelect.propTypes = {
    options: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string.isRequired,
            label: PropTypes.node.isRequired,
            disabled: PropTypes.bool,
            /** Why this option is unavailable; surfaced as the chip's tooltip. */
            title: PropTypes.string,
        })
    ),
    value: PropTypes.string,
    onChange: PropTypes.func.isRequired,
    label: PropTypes.node,
    /** Put the label on the same line as the chips (the prototype's layout). */
    inline: PropTypes.bool,
};

ChipSingleSelect.defaultProps = {
    options: [],
    value: null,
    label: null,
    inline: false,
};

export default ChipSingleSelect;
